"use client";
import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/Input";
import { useTranslations } from "@/i18n/client";
import { useLogin } from "@frend-digital/centra/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { ErrorText, Link, Title } from "../Components";

import { useRouter } from "@/i18n/routing";
import { ACCOUNT_PATHS } from "../config";
import styles from "./Login.module.css";
import { useAccountModal } from "../AccountModal";
import { useGlobal } from "@/features/global/GlobalContext";
import { addLeadingSlash } from "@frend-digital/utils";
import { useTransition } from "react";
import { useAccountNavigate } from "../hooks";

export type LoginFormProps = {
	modal?: boolean;
};

export const LoginForm = ({ modal }: LoginFormProps) => {
	const t = useTranslations();
	const login = useLogin();
	const [, setOpen] = useAccountModal();
	const [pending, navigate] = useAccountNavigate({ modal });

	const schema = z.object({
		email: z.string().email({ message: t("Invalid email") }),
		password: z.string({ message: t("Password is required") }),
	});

	const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema) });

	const handleSubmit = (data: z.infer<typeof schema>) => {
		login.mutate(data, {
			onSuccess: async () => {
				navigate();
			},
		});
	};

	return (
		<FormProvider {...form}>
			<form className={styles.form} onSubmit={form.handleSubmit(handleSubmit)}>
				{!modal && (
					<Title>
						<Translate>Login</Translate>
					</Title>
				)}
				{login.error && (
					<ErrorText>
						<Translate>Invalid credentials</Translate>
					</ErrorText>
				)}

				<FormField
					name="email"
					placeholder={t("Enter your email-address")}
					label={<Translate>Email</Translate>}
				/>
				<FormField
					type="password"
					name="password"
					placeholder={t("Enter your password")}
					label={<Translate>Password</Translate>}
				/>

				<Link style={{ textAlign: "right" }} href={ACCOUNT_PATHS.FORGOT_PASSWORD}>
					<Translate>Forgot password?</Translate>
				</Link>
				<Button
					color="red"
					type="submit"
					loading={login.isPending || pending}
					className={styles.mobileButton}
				>
					<Translate>Login</Translate>
				</Button>

				<Button
					href={!modal ? ACCOUNT_PATHS.REGISTER : undefined}
					color="beige"
					type="button"
					onClick={modal ? () => setOpen(true, "register") : undefined}
					className={styles.mobileButton}
				>
					<Translate>Register</Translate>
				</Button>
			</form>
		</FormProvider>
	);
};
